import { AmpNavigationV2 as AmpNavigation } from '@amplify/amp-navigation-v2';
import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useEventListenerRef, useMergeRefs } from 'rooks';

import AuthService from 'lib/authService';
import { GAME_MODE, PRODUCT_LECTURA, PRODUCT_NOT_YET_SELECTED } from 'lib/constants';
import { logOutUser } from 'a/api/logout';
import { openGlobalNavLogoutModal, closeGlobalNavLogoutModal } from 'a/hub/actions/modalSystemActions';
import { getGameMode, getAssignmentLinkNavStatus } from 'a/hub/selectors';
import { getProduct } from 'a/selectors/languageSelectors';
import { getIsThisTypeOfModalOpen } from 'a/hub/selectors/modalSystemSelectors';
import { KEYCODES } from 'a/data/constants';
import { GLOBAL_NAV_SCREENS } from 'a/hub/data/constants';
import GameIds from 'a/hub/data/GameIds';
import { MODAL_TYPES } from 'a/components/ModalSystem';

// NAV CONFIG //
const getAmpNavConfig = (product) => {
  const kc = AuthService.getKC();
  const isTeacher = AuthService.getRoles().includes('TEACHER');
  return {
    _keycloak: kc,
    isStudentPreviewMode: isTeacher, // used for teacher demo mode only (nav not displayed in teacher deep link)
    isStudentMiddleSchoolView: false, // used for teacher demo mode to display elementary school nav
    logoutLanguage: product === PRODUCT_LECTURA ? 'ES' : 'EN',
    customLogoutHandler: logOutUser,
    isMobileNavUIEnabled: false,
  };
};

const AmpGlobalNav = () => {
  const dispatch = useDispatch();

  // SELECTORS //
  const product = useSelector(getProduct);
  const isNavLogoutModalOpen = useSelector(state =>
    getIsThisTypeOfModalOpen(state, MODAL_TYPES.GLOBAL_NAV_LOGOUT),
  );
  const shouldShowAmpNav = useSelector(state => (
    [
      GLOBAL_NAV_SCREENS.MAP,
      GLOBAL_NAV_SCREENS.WORKSHOP,
      GLOBAL_NAV_SCREENS.PROGRESS_SCREEN,
    ].includes(state.metaGameReducer.navigation.screen)
    && state.selectedActivity.activity === GameIds.HUB
  ) || (
    GLOBAL_NAV_SCREENS.LOADING === state.metaGameReducer.navigation.screen
    && state.appSessionReducer.product === PRODUCT_NOT_YET_SELECTED
  ) || (
    [GameIds.PLACEMENT_TOOL, GameIds.TRO_BENCHMARK].includes(state.selectedActivity.activity)
    && state.placementTool.shouldDisplayAmpGlobalNav
  ) || (
    state.selectedActivity.activity === GameIds.SKILL_SCAN
    && state.skillScan.shouldDisplayAmpGlobalNav
  ) || (
    getGameMode(state) === GAME_MODE.ASSIGNMENT_LINK && getAssignmentLinkNavStatus(state)
  ));

  // Shift app to top when nav not rendered (for small screens only)
  useEffect(() => {
    const reactWrapper = document.getElementById('reactWrapper');
    if (reactWrapper) {
      if (shouldShowAmpNav) {
        reactWrapper.classList.remove('hide-global-nav');
      } else {
        reactWrapper.classList.add('hide-global-nav');
      }
    }
  }, [shouldShowAmpNav]);

  // NAV EVENT CALLBACKS //
  const handleNavEvents = (event) => {
    const isClickEvent = event.type === 'click';
    const isKeyDownEvent = event.type === 'keydown';
    const isActivationEvent = isClickEvent || (isKeyDownEvent && [KEYCODES.ENTER, KEYCODES.SPACE].includes(event.code));
    const isEscapeKeyEvent = isKeyDownEvent && event.code === KEYCODES.ESC;

    // Open logout modal via logout button on nav
    if (
      !isNavLogoutModalOpen &&
      isActivationEvent &&
      event.target.closest('#k5-logout-button') // logout button
    ) {
      return dispatch(openGlobalNavLogoutModal());
    }

    // Close logout modal via Esc key | select 'X' or 'Go back' on logout modal | click outside modal
    if (
      isNavLogoutModalOpen &&
      (isEscapeKeyEvent ||
        (isActivationEvent &&
          (
            event.target.closest('button.nav-button.logout-modal__close-button') || // 'X' on logout modal
            event.target.closest('button.nav-button.logout-modal__back-button') || // 'Go back' on logout modal
            event.target.closest('div.logout-modal__overlay') // click outside logout modal
          )
        )
      )
    ) {
      return dispatch(closeGlobalNavLogoutModal());
    }
    return null;
  };

  const clickRef = useEventListenerRef('click', handleNavEvents);
  const keydownRef = useEventListenerRef('keydown', handleNavEvents);
  const navEventsRef = useMergeRefs(clickRef, keydownRef);

  const ampNavConfig = useMemo(() => getAmpNavConfig(product), [product]);
  return shouldShowAmpNav ? (
    <div ref={navEventsRef} data-dd-action-name="amp-navigation">
      <AmpNavigation initialConfig={ampNavConfig} />
    </div>
  ) : null;
};

export default AmpGlobalNav;
